import * as Y from 'yjs';
import type { DocumentAdapter } from '../domain/adapter.js';
import type { StoreContext } from '../domain/store.js';
import type { Unsubscribe } from '../domain/types.js';
import { YjsDocumentAdapter } from './adapter.js';
import { runMigrations } from './migrations/index.js';
import type { MigrationResult } from './migrations/types.js';

export type UpdateListener = (update: Uint8Array, origin: unknown) => void;

/**
 * An opaque handle to a household document.
 *
 * Everything outside packages/core/src/yjs — the sync client, the Worker, both UIs —
 * moves documents around through this interface, dealing only in byte arrays. That is what
 * lets the CRDT stay swappable: nothing else in the codebase knows Yjs exists.
 */
export interface DocumentHandle {
  readonly documentId: string;
  applyUpdate(update: Uint8Array, origin?: string): void;
  /** Full state, or just what the peer is missing when given their state vector. */
  encodeStateAsUpdate(sinceStateVector?: Uint8Array): Uint8Array;
  encodeStateVector(): Uint8Array;
  onUpdate(listener: UpdateListener): Unsubscribe;
  /** Memoized: repeated calls return the same adapter, so listeners cannot accumulate. */
  createAdapter(): DocumentAdapter;
  /** Brings the document up to the current schema version. Idempotent. */
  migrate(context: Pick<StoreContext, 'now' | 'userId'>): MigrationResult;
  isEmpty(): boolean;
  destroy(): void;
}

class YDocHandle implements DocumentHandle {
  private adapter: YjsDocumentAdapter | null = null;

  constructor(
    readonly documentId: string,
    readonly doc: Y.Doc,
  ) {}

  applyUpdate(update: Uint8Array, origin = 'remote'): void {
    Y.applyUpdate(this.doc, update, origin);
  }

  encodeStateAsUpdate(sinceStateVector?: Uint8Array): Uint8Array {
    return Y.encodeStateAsUpdate(this.doc, sinceStateVector);
  }

  encodeStateVector(): Uint8Array {
    return Y.encodeStateVector(this.doc);
  }

  onUpdate(listener: UpdateListener): Unsubscribe {
    const handler: UpdateListener = (update, origin) => listener(update, origin);
    this.doc.on('update', handler);
    return () => this.doc.off('update', handler);
  }

  createAdapter(): DocumentAdapter {
    this.adapter ??= new YjsDocumentAdapter(this.doc);
    return this.adapter;
  }

  migrate(context: Pick<StoreContext, 'now' | 'userId'>): MigrationResult {
    return runMigrations(this.doc, context);
  }

  isEmpty(): boolean {
    return this.doc.getMap('recipes').size === 0 && this.doc.getMap('meta').size === 0;
  }

  destroy(): void {
    this.adapter?.destroy();
    this.adapter = null;
    this.doc.destroy();
  }
}

export function createDocument(documentId: string): DocumentHandle {
  // Garbage collection stays on. Revision history is derived server-side precisely so we
  // never have to set `gc: false`, which would let tombstones accumulate in a document
  // that every phone in the household carries.
  return new YDocHandle(documentId, new Y.Doc({ gc: true }));
}

export function openDocument(documentId: string, ...updates: readonly Uint8Array[]): DocumentHandle {
  const handle = createDocument(documentId);
  for (const update of updates) handle.applyUpdate(update, 'load');
  return handle;
}

/**
 * Collapses a pile of incremental updates into one compact update.
 *
 * `Y.mergeUpdates` alone would be cheaper but it does not garbage-collect: it concatenates
 * history without dropping deleted content, so a long-lived document would grow without
 * bound. Round-tripping through a real Y.Doc is the only way to actually reclaim space,
 * which is why both the client persistence layer and the Durable Object call this.
 */
export function compactUpdates(updates: readonly Uint8Array[]): Uint8Array {
  const doc = new Y.Doc({ gc: true });
  doc.transact(() => {
    for (const update of updates) Y.applyUpdate(doc, update, 'compaction');
  });
  const compacted = Y.encodeStateAsUpdate(doc);
  doc.destroy();
  return compacted;
}

/** State vector of a stored update, without keeping the document in memory. */
export function stateVectorOf(update: Uint8Array): Uint8Array {
  return Y.encodeStateVectorFromUpdate(update);
}

/** The part of `update` a peer with `stateVector` has not seen. */
export function diffUpdate(update: Uint8Array, stateVector: Uint8Array): Uint8Array {
  return Y.diffUpdate(update, stateVector);
}

export function mergeUpdates(updates: readonly Uint8Array[]): Uint8Array {
  return Y.mergeUpdates([...updates]);
}
